import { assetSortSchema, sortDirSchema, type AssetSort, type SortDir } from '@xphoto/shared';

/**
 * Préférences UTILISATEUR d'ORDRE DE TRI de la galerie, stockées CÔTÉ CLIENT en
 * `localStorage` — même patron que le thème (`theme/theme.ts`), la langue
 * (`i18n/detect.ts`) et la densité (`density-pref.ts`), sans endpoint serveur.
 *
 * DEUX emplacements (`SortScope`), et volontairement PAS une carte de contextes :
 *
 *  - `home`   — la route `/` (onglet Galerie), QUELS QUE SOIENT ses filtres ;
 *  - `browse` — tout le reste (`/dossiers`, `/tags`). C'est le tri « COLLANT » : le
 *    dernier choisi s'applique partout jusqu'à nouvel ordre.
 *
 * POURQUOI l'accueil fait EXCEPTION : c'est la seule vue dont le métier diffère —
 * une surface « montre-moi quelque chose » (d'où l'aléatoire par défaut, la graine
 * persistée par compte et le bouton « Re-mélanger », que rien d'autre n'a) — là où un
 * dossier est un endroit où l'on VA CHERCHER quelque chose et veut un ordre stable.
 *
 * POURQUOI PAS une mémoire de tri PAR DOSSIER (conception envisagée puis ÉCARTÉE avec
 * l'utilisateur) : elle imposait des règles d'arbitrage inexplicables (« quel tri pour
 * un dossier jamais visité ? celui du parent ? »), des clés dérivées de chemins qui
 * cassent au renommage, un plafond de stockage et une UI « oublier ce dossier ».
 *
 * Les deux valeurs sont INDÉPENDANTES (trier depuis l'accueil ne touche pas le tri de
 * navigation, et réciproquement) et partent toutes deux d'`Aléatoire` : sur un
 * navigateur neuf, rien ne change pour qui n'y touche pas.
 *
 * La préférence retient le COUPLE `{ sort, dir }`, pas le seul critère : « date
 * INVERSÉE » — l'exemple de l'utilisateur — est une DIRECTION, et la déduire de
 * `defaultDirForSort` perdait la moitié de son choix.
 *
 * Module PUR & testé, tolérant si `localStorage` est indisponible (mode privé strict,
 * quotas) : repli en mémoire, jamais d'exception. Une valeur stockée corrompue ⇒
 * défaut de fabrique, jamais propagée (validation par les schémas de contrat).
 */

/** Les deux emplacements de préférence de tri (cf. `sortScopeFor` pour le chemin → portée). */
export type SortScope = 'home' | 'browse';

/** Couple PERSISTÉ : critère + sens. */
export interface SortPref {
  sort: AssetSort;
  dir: SortDir;
}

/** Clés de persistance (namespace `xphoto.*`, comme le thème et la densité). */
export const SORT_PREF_STORAGE_KEY: Record<SortScope, string> = {
  home: 'xphoto.gallery.sort.home',
  browse: 'xphoto.gallery.sort.browse',
};

/**
 * ANCIENNE clé (≤ v1.23), qui ne stockait QUE le critère, en chaîne nue (`"filename"`).
 * Relue comme tri d'ACCUEIL quand la nouvelle clé est absente, avec la direction par
 * défaut du critère — MIGRATION SILENCIEUSE : personne ne se retrouve réinitialisé.
 *
 * Elle n'est jamais RÉÉCRITE ni effacée : la laisser en place ne coûte rien (la
 * nouvelle clé gagne toujours) et garde un retour arrière de version fonctionnel.
 */
export const LEGACY_DEFAULT_SORT_STORAGE_KEY = 'xphoto.gallery.defaultSort';

/** Défaut de fabrique : ALÉATOIRE, pour les DEUX emplacements. */
export const DEFAULT_SORT_FALLBACK: AssetSort = 'random';

/**
 * Direction par défaut HABITUELLE d'un tri déterministe (ignorée pour `random`) :
 * dates & mesures « du plus récent / plus grand d'abord » (desc), nom alphabétique
 * (asc). Aligné sur l'ancien défaut galerie (`takenAt` desc).
 */
export function defaultDirForSort(sort: AssetSort): SortDir {
  switch (sort) {
    case 'filename':
      return 'asc';
    default:
      // takenAt / createdAt / sizeBytes / viewCount / rating (et random, inutilisé) → desc.
      return 'desc';
  }
}

/** Couple par défaut de fabrique (aléatoire), partagé par les deux emplacements. */
export const SORT_PREF_FALLBACK: SortPref = {
  sort: DEFAULT_SORT_FALLBACK,
  dir: defaultDirForSort(DEFAULT_SORT_FALLBACK),
};

/**
 * Valeurs proposées à l'utilisateur pour l'ordre par défaut de l'accueil, dans l'ordre
 * d'affichage du `<select>` des Paramètres. Toutes ∈ `assetSortSchema` (allow-list de
 * contrat). « Aléatoire » en tête (défaut) ; puis les tris déterministes usuels.
 */
export const DEFAULT_SORT_OPTIONS: readonly AssetSort[] = [
  'random',
  'filename',
  'takenAt',
  'createdAt',
  'sizeBytes',
  'viewCount',
  'rating',
];

/** Repli mémoire PAR EMPLACEMENT quand `localStorage` est inaccessible (jamais d'exception). */
const memoryPref: Record<SortScope, SortPref | null> = { home: null, browse: null };

/** Valide un critère brut contre l'allow-list de tri, ou `null`. */
function parseSort(raw: unknown): AssetSort | null {
  const parsed = assetSortSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Valide un sens brut contre l'allow-list de direction, ou `null`. */
function parseDir(raw: unknown): SortDir | null {
  const parsed = sortDirSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Décode la valeur persistée (JSON `{"sort":…,"dir":…}`) en couple VALIDE, ou `null`.
 * Tout ce qui n'est pas un couple entièrement valide (JSON illisible, champ manquant,
 * valeur hors allow-list) est traité comme ABSENT → défaut de fabrique.
 */
function parseStoredPref(raw: string | null): SortPref | null {
  if (raw === null) return null;
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof decoded !== 'object' || decoded === null) return null;
  const record = decoded as Record<string, unknown>;
  const sort = parseSort(record.sort);
  const dir = parseDir(record.dir);
  return sort === null || dir === null ? null : { sort, dir };
}

/**
 * Lit la préférence persistée d'un emplacement, ou `null` si absente/invalide. Le repli
 * MÉMOIRE n'est consulté QUE si `localStorage` est INDISPONIBLE (absent ou en erreur) —
 * pas quand il renvoie simplement `null` (aucune préférence) : ainsi vider le stockage
 * remet bien au défaut, et le repli ne masque pas l'absence de valeur.
 */
export function readStoredSortPref(scope: SortScope): SortPref | null {
  try {
    if (typeof localStorage === 'undefined') return memoryPref[scope];
    const stored = parseStoredPref(localStorage.getItem(SORT_PREF_STORAGE_KEY[scope]));
    if (stored !== null) return stored;
    // MIGRATION SILENCIEUSE de l'ancienne clé (critère nu) → tri d'ACCUEIL uniquement :
    // c'est bien la préférence que le réglage « Ordre par défaut » éditait jusqu'ici.
    if (scope !== 'home') return null;
    const legacy = parseSort(localStorage.getItem(LEGACY_DEFAULT_SORT_STORAGE_KEY));
    return legacy === null ? null : { sort: legacy, dir: defaultDirForSort(legacy) };
  } catch {
    return memoryPref[scope];
  }
}

/** Préférence EFFECTIVE d'un emplacement : persistée sinon le défaut de fabrique. */
export function getSortPref(scope: SortScope): SortPref {
  return readStoredSortPref(scope) ?? SORT_PREF_FALLBACK;
}

/**
 * Les DEUX préférences effectives, sous la forme attendue par `SortResolutionContext` —
 * seul point où le résolveur (PUR) reçoit le contenu du `localStorage` :
 * `resolveGalleryHref(href, { ...getSortPrefs(), accountSeed })`.
 */
export function getSortPrefs(): { homePref: SortPref; browsePref: SortPref } {
  return { homePref: getSortPref('home'), browsePref: getSortPref('browse') };
}

/**
 * Persiste le couple d'un emplacement (repli mémoire + `localStorage`, silencieux si
 * indisponible). Un couple hors allow-list est ignoré (garde-fou runtime).
 */
export function storeSortPref(scope: SortScope, pref: SortPref): void {
  const sort = parseSort(pref.sort);
  const dir = parseDir(pref.dir);
  if (sort === null || dir === null) return;
  const valid: SortPref = { sort, dir };
  memoryPref[scope] = valid;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SORT_PREF_STORAGE_KEY[scope], JSON.stringify(valid));
    }
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}
